import { FC, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'antd';
import { isNotEmpty, isNotEmptyAny } from '@lhb/func';
import { fixNumber } from '@/common/utils/ways';
import HeatMap from '@/common/components/business/PopulationHeatMap/HeatMap';
import { industryCodeEnum } from '@/views/iterate/pages/siteselectionmapb/ts-config';
import styles from './index.module.less';
import { BisConType } from './ts-config';
import Group from './Group';
import GroupHorizontal from './GroupHorizontal';
import BlockHorizontal from './BlockHorizontal';
import { getPopulationInfo } from '@/common/api/youth';
import { getRadiusByRangeType, RangeType } from '../../ts-config';
import { useMethods } from '@lhb/hook';
import { highlightSelectMarker } from 'src/common/components/business/YouthMap/AreaDetailDrawer/components/FirstScreenMap/Rail';

/**
 * @Description 商圈详情-人口数据及人口热力图
 */
const Basic: FC<{
  overView,
  detail,
  mapIns,
  /**
   * 商圈围栏，
   * 内含：markerOf100、markerOf200、markerOf300、markerOf500、markerOf500Walking、markerOf1000、markerOf1500Living、markerOf1500、markerOf3000Takeaway、markerOf3000
   * 来自 src/common/components/business/YouthMap/AreaDetailDrawer/components/FirstScreenMap/Rail.tsx
   * 最终用在 src/common/components/business/YouthMap/AreaDetailDrawer/components/FirstScreenRight/components/Basic/index.tsx
   */
  rangeMarkerRef,
  setIsOpenHeatMap,
  isShare,
  rangeType: RangeType,
}> = ({
  overView,
  detail,
  mapIns,
  rangeMarkerRef,
  setIsOpenHeatMap,
  isShare,
  rangeType,
}) => {
  const [populationInfo, setPopulationInfo] = useState<any>(null);

  const [active, setActive] = useState<any>({
    keyName: '',
    /** 选中的距离 code */
    radiusCode: null,
    radius: 0,
    /** 可能是用于选中当前点击的热力图的参数 */
    basicParams: {}
  });// 选中的热力

  const populationStr = (count) => {
    if (!isNotEmpty(count)) return '-';
    // return +count >= 10000
    //   ? `${beautifyThePrice(count / 10000.0, ',', 1)}`
    //   : beautifyThePrice(Math.floor(count), ',', 0);
    return +count >= 10000
      ? `${fixNumber(count / 10000, 1)}`
      : Math.floor(count);
  };

  const targetUnit = (count) => {
    if (!isNotEmpty(count)) return '';
    return +count >= 10000 ? '万人' : '人';
  };

  const populationData = useMemo(() => {
    // 产品要求，在 detail.resourceMallFlag 这种情况下会在外部展示 MallModule，里面包含了商场客流，所以这里不展示
    const showMallFlow = !!overView?.mallPassFlow && !detail?.resourceMallFlag;
    const passengerFlow: any = showMallFlow ? {
      desc: '商场日均客流',
      title: '商场日均客流',
      value: populationStr(overView?.mallPassFlow),
      unit: targetUnit(overView?.mallPassFlow),
      basicValue: overView?.mallPassFlow,
      tag: overView?.flowRank,
      keyName: 'mallPassFlow',
    } : null;

    const { industryCode } = overView || {};
    // 判断属于汽车行业且有值，才展示有车比例
    const showCarRate = [industryCodeEnum.carSale, industryCodeEnum.carRepair].includes(industryCode) && !!overView?.carRate;
    const carRate: any = showCarRate ? {
      desc: '有车客群比例',
      title: '有车客群比例',
      value: overView?.carRate ? overView?.carRate + '%' : '-',
      basicValue: overView?.carRate,
      tag: overView?.carRateRank,
      keyName: 'carRate',
    } : null;

    const res = {
      carRate: carRate,
      passengerFlow: passengerFlow,
      livePopulations: (Array.isArray(populationInfo?.livePopulations) ? populationInfo.livePopulations : []).map(item => ({
        desc: `周边${item.name}居住人口`,
        title: item.name,
        value: populationStr(item.population),
        unit: targetUnit(item.population),
        basicValue: item.population,
        // tag: populationInfo?.populationRank,
        isHeatMap: item.code !== RangeType.DESIGNATE && (item.radius || getRadiusByRangeType(item.code)) > 0, // 围栏内不支持热力图
        radius: item.radius || getRadiusByRangeType(item.code),
        radiusCode: item.code,
        keyName: `livePopulations_${item.code}`,
        basicParams: {
          queryType: 1,
        }
      })),
      workPopulations: (Array.isArray(populationInfo?.workPopulations) ? populationInfo.workPopulations : []).map(item => ({
        desc: `周边${item.name}工作人口`,
        title: item.name,
        value: populationStr(item.population),
        unit: targetUnit(item.population),
        basicValue: item.population,
        // tag: populationInfo?.populationRank,
        isHeatMap: item.code !== RangeType.DESIGNATE && (item.radius || getRadiusByRangeType(item.code)) > 0, // 围栏内不支持热力图
        radius: item.radius || getRadiusByRangeType(item.code),
        radiusCode: item.code,
        keyName: `workPopulations_${item.code}`,
        basicParams: {
          queryType: 2,
        }
      })),
      residentPopulations: (Array.isArray(populationInfo?.residentPopulations) ? populationInfo.residentPopulations : []).map(item => ({
        desc: `周边${item.name}常住人口`,
        title: item.name,
        value: populationStr(item.population),
        unit: targetUnit(item.population),
        basicValue: item.population,
        // tag: populationInfo?.populationRank,
        isHeatMap: item.code !== RangeType.DESIGNATE && (item.radius || getRadiusByRangeType(item.code)) > 0, // 围栏内不支持热力图
        radius: item.radius || getRadiusByRangeType(item.code),
        radiusCode: item.code,
        keyName: `residentPopulations_${item.code}`,
        basicParams: {
          queryType: 3,
        }
      })),
    };
    return res;
  }, [overView, populationInfo]);

  /**
   * @description 不展示汽车保有率、与商场客流数据
   */
  const isNoCarNoPassenger = useMemo(() => {
    return populationData && !isNotEmptyAny(populationData.carRate) && !isNotEmptyAny(populationData.passengerFlow);
  }, [populationData]);

  /**
   * @description 展示汽车保有率、或商场客流数据中的至少一个
   */
  const isHasCarOrMall = useMemo(() => {
    return populationData && (isNotEmptyAny(populationData.carRate) || isNotEmptyAny(populationData.passengerFlow));
  }, [populationData]);

  /* hooks */
  useEffect(() => {
    setTimeout(() => {
      // 人口数据选择范围的时候高亮对应围栏
      highlightSelectMarker({ rangeType: active?.radiusCode, rangeMarkerRef, mapIns });
    }, 10);
  }, [active.radiusCode]);

  // 当距离改变后，请求接口
  useEffect(() => {
    setTimeout(() => {
      methods.getPopulationInfo();
    }, 100);
  }, [rangeType, overView?.id]);

  const methods = useMethods({

    getPopulationInfo() {
      if (!overView?.id) return;
      getPopulationInfo({
        id: overView?.id,
        rangeType: rangeType,
      }).then((val) => {
        setPopulationInfo(val);
      });
    },

    onHeatEvent: (checked: boolean, item: BisConType) => {
      // 如果是取消，或者数据不存在则取消
      if (!checked || !(item.isHeatMap/* && item?.basicValue*/)) {
        setActive({
          keyName: '',
          basicParams: {},
          radius: 0
        });
        setIsOpenHeatMap(false);
        return;
      }
      setIsOpenHeatMap(true);
      setActive({
        keyName: item.keyName,
        radiusCode: item.radiusCode,
        radius: item.radius,
        basicParams: item.basicParams,
      });
    },

    /** 人口数据纵向展示 */
    renderNoCarNoMall: () => {
      return (
        <Row gutter={12}>
          <Col span={8}>
            <Group
              groupName='居住人口'
              list={populationData?.livePopulations}
              active={active}
              onHeatEvent={methods.onHeatEvent}/>
          </Col>
          <Col span={8}>
            <Group
              groupName='办公人口'
              list={populationData?.workPopulations}
              active={active}
              onHeatEvent={methods.onHeatEvent}/>
          </Col>
          <Col span={8}>
            <Group
              groupName='常住人口'
              list={populationData?.residentPopulations}
              active={active}
              onHeatEvent={methods.onHeatEvent}/>
          </Col>
        </Row>
      );
    },

    /** 人口数据横向展示 */
    renderHasCarOrMall: () => {

      const renderPassengerFlowOrCarRate = () => {
        if (isNotEmptyAny(populationData.passengerFlow) && isNotEmptyAny(populationData.carRate)) {
          return <Row gutter={8}>
            <Col span={12}>
              <BlockHorizontal data={populationData.passengerFlow}/>
            </Col>
            <Col span={12}>
              <BlockHorizontal data={populationData.carRate}/>
            </Col>
          </Row>;
        } else if (isNotEmptyAny(populationData.passengerFlow)) {
          return <BlockHorizontal data={populationData.passengerFlow} iconSrc={`${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/selectionmap/icon_population_flow.png`}/>;
        } else if (isNotEmptyAny(populationData.carRate)) {
          return <BlockHorizontal data={populationData.carRate} iconSrc={`${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/selectionmap/icon_population_car.png`}/>;
        } else {
          return <></>;
        }
      };

      return (
        <Row gutter={[8, 8]}>
          <Col span={12}>{renderPassengerFlowOrCarRate()}</Col>
          <Col span={12}>
            <GroupHorizontal
              groupName='居住人口'
              list={populationData?.livePopulations}
              active={active}
              onHeatEvent={methods.onHeatEvent}/>
          </Col>
          <Col span={12}>
            <GroupHorizontal
              groupName='办公人口'
              list={populationData?.workPopulations}
              active={active}
              onHeatEvent={methods.onHeatEvent}/>
          </Col>
          <Col span={12}>
            <GroupHorizontal
              groupName='常住人口'
              list={populationData?.residentPopulations}
              active={active}
              onHeatEvent={methods.onHeatEvent}/>
          </Col>
        </Row>
      );
    },

  });

  return (<div>
    <div className={styles.basicContainer}>
      {isNoCarNoPassenger ? methods.renderNoCarNoMall() : <></>}
      {isHasCarOrMall ? methods.renderHasCarOrMall() : <></>}
    </div>
    <HeatMap
      mapIns={mapIns}
      active={active}
      detail={detail}
      isShare={isShare}
    />
  </div>);
};

export default Basic;
